import { eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, startOfMonth, startOfWeek, parseISO } from "date-fns";
import { toISO, todayISO } from "@/lib/format";
import { useSettings } from "@/context/SettingsContext";
import { jobTypeColor } from "@/lib/jobTypes";
import { cn } from "@/lib/utils";

export const loadLevel = (count, capacity) => {
  if (!count) return { key: "none", label: "No jobs", bar: "bg-slate-200" };
  const r = count / capacity;
  if (r > 1) return { key: "over", label: "Overbooked", bar: "bg-rose-500", text: "text-rose-700" };
  if (r >= 0.85) return { key: "full", label: "Near capacity", bar: "bg-orange-500", text: "text-orange-700" };
  if (r >= 0.6) return { key: "busy", label: "Busy", bar: "bg-amber-400", text: "text-amber-700" };
  return { key: "ok", label: "Available", bar: "bg-sky-500", text: "text-sky-700" };
};

export const MonthGrid = ({ anchor, selected, onSelect, days, capacity }) => {
  const { settings } = useSettings();
  const grid = eachDayOfInterval({ start: startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 }) });
  const today = todayISO();
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4" data-testid="calendar-month">
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.map((d) => {
          const iso = toISO(d);
          const info = days[iso];
          const lvl = loadLevel(info?.count || 0, capacity);
          const types = Object.entries(info?.types || {}).sort((a, b) => b[1] - a[1]);
          return (
            <button key={iso} onClick={() => onSelect(iso)} data-testid={`calendar-day-${iso}`}
              className={cn("relative flex min-h-[64px] flex-col rounded-lg border border-slate-100 p-1.5 text-left transition-colors hover:border-sky-400 sm:min-h-[92px]",
                !isSameMonth(d, anchor) && "opacity-40", selected === iso && "ring-2 ring-[#0E1E38] ring-offset-1", lvl.key === "over" && "bg-rose-50")}>
              <div className="flex w-full items-center justify-between">
                <span className={cn("text-xs font-semibold", iso === today ? "rounded bg-[#FACC15] px-1 text-[#0E1E38]" : "text-slate-600")}>{format(parseISO(iso), "d")}</span>
                {info?.count > 0 && <span className={cn("font-mono text-[11px] font-bold", lvl.text)} data-testid={`calendar-count-${iso}`}>{info.count}</span>}
              </div>
              <div className="mt-1 flex flex-wrap gap-1" data-testid={`calendar-dots-${iso}`}>
                {types.map(([name, n]) => (
                  <span key={name} title={`${name} × ${n}`} className="inline-flex h-2.5 min-w-[10px] items-center justify-center rounded-full px-0.5 text-[8px] font-bold leading-none text-white sm:h-3.5 sm:min-w-[14px] sm:text-[9px]"
                    style={{ background: jobTypeColor(settings.job_types, name === "Unassigned" ? "" : name) }}>
                    <span className="hidden sm:inline">{n > 1 ? n : ""}</span>
                  </span>
                ))}
              </div>
              {info?.count > 0 && (
                <span className="mt-auto block h-1 w-full overflow-hidden rounded bg-slate-100">
                  <span className={cn("block h-full", lvl.bar)} style={{ width: `${Math.min((info.count / capacity) * 100, 100)}%` }} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
